import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { getUserPermissions, updateUser, updateUserPermissions } from '../api/usersApi';
import { PERMISSION_GROUPS, ROLES, ROLE_DEFAULT_PERMISSIONS, getRoleLabel } from '../model/userConstants';
import { usePermissions } from '../../../shared/auth/PermissionsContext';

/**
 * Diálogo de gestión de permisos de un usuario: selector de rol y
 * interruptores por funcionalidad. Los cambios respecto a los permisos
 * por defecto del rol se guardan como excepciones (concesión/revocación).
 */
export default function UserPermissionsDialog({ user, open, onClose, onSaved }) {
  const { hasPermission, refreshPermissions } = usePermissions();
  const canManageAdmins = hasPermission('users:manage_admins');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [role, setRole] = useState('SANITARIO');
  const [roleDefaults, setRoleDefaults] = useState([]);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    if (!open || !user) return;
    setError('');
    setLoading(true);
    getUserPermissions(user.id)
      .then((data) => {
        setRole(data.role);
        setRoleDefaults(data.roleDefaults || []);
        const next = {};
        PERMISSION_GROUPS.forEach((group) => {
          group.permissions.forEach(({ key }) => {
            next[key] = (data.effective || []).includes(key);
          });
        });
        setChecked(next);
      })
      .catch((err) => {
        console.error('Error al cargar los permisos del usuario:', err);
        setError('No se pudieron cargar los permisos del usuario.');
      })
      .finally(() => setLoading(false));
  }, [open, user]);

  const isSuperadmin = role === 'SUPERADMIN';

  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    // Al cambiar de rol se parte de sus defaults, sin excepciones previas
    const defaults = ROLE_DEFAULT_PERMISSIONS[nextRole] || [];
    setRoleDefaults(defaults);
    const next = {};
    PERMISSION_GROUPS.forEach((group) => {
      group.permissions.forEach(({ key }) => {
        next[key] = defaults.includes(key);
      });
    });
    setChecked(next);
  };

  const overrides = useMemo(() => {
    if (isSuperadmin) return [];
    const result = [];
    PERMISSION_GROUPS.forEach((group) => {
      group.permissions.forEach(({ key }) => {
        const byDefault = roleDefaults.includes(key);
        const wanted = !!checked[key];
        if (wanted !== byDefault) {
          result.push({ permission: key, granted: wanted });
        }
      });
    });
    return result;
  }, [checked, roleDefaults, isSuperadmin]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (user.role !== role) {
        await updateUser({ userId: user.id, payload: { role } });
      }
      if (!isSuperadmin) {
        await updateUserPermissions({ userId: user.id, overrides });
      }
      await refreshPermissions();
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error al guardar los permisos:', err);
      setError(err?.response?.data?.message || 'No se pudieron guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Permisos de {user.nombre || user.username}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <FormControl fullWidth margin="normal">
              <InputLabel>Rol</InputLabel>
              <Select
                value={role}
                label="Rol"
                disabled={!canManageAdmins}
                onChange={(e) => handleRoleChange(e.target.value)}
              >
                {Object.keys(ROLES).map((key) => (
                  <MenuItem key={key} value={key}>{getRoleLabel(key)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {!canManageAdmins ? (
              <Typography variant="caption" color="text.secondary">
                Solo un administrador total puede cambiar el rol.
              </Typography>
            ) : null}

            {isSuperadmin ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Un administrador total siempre tiene todos los permisos.
              </Alert>
            ) : (
              PERMISSION_GROUPS.map((group) => (
                <Box key={group.module} sx={{ mt: 2 }}>
                  <Divider textAlign="left"><Typography variant="subtitle2">{group.module}</Typography></Divider>
                  {group.permissions.map(({ key, label }) => {
                    const byDefault = roleDefaults.includes(key);
                    const isException = !!checked[key] !== byDefault;
                    return (
                      <Box key={key} display="flex" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={!!checked[key]}
                              onChange={(e) => setChecked((prev) => ({ ...prev, [key]: e.target.checked }))}
                            />
                          )}
                          label={label}
                        />
                        {isException ? (
                          <Chip size="small" color="warning" label={checked[key] ? 'Concedido' : 'Revocado'} />
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
